import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import React, { useCallback, useMemo, useState } from 'react';

import { HGrid, VStack } from '@navikt/ds-react';

import { Month } from './Month';
import { CalendarPeriod } from './types/CalendarPeriod';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);

interface Props {
    periods: CalendarPeriod[];
    showWeekNumbers?: boolean;
    dateTooltipCallback?: (date: string) => React.ReactElement | string;
    setSelectedPeriods?: (value: React.SetStateAction<CalendarPeriod[]>) => void;
    isRangeSelection?: boolean;
    getSrTextForSelectedPeriod?: (period: { fom: string; tom: string }) => string;
    firstDateInCalendar: string;
    lastDateInCalendar?: string;
}

export const Calendar = ({
    periods,
    showWeekNumbers = true,
    dateTooltipCallback,
    setSelectedPeriods,
    isRangeSelection = false,
    getSrTextForSelectedPeriod,
    firstDateInCalendar,
    lastDateInCalendar,
}: Props) => {
    const allMonths = useMemo(
        () => findMonths(firstDateInCalendar, lastDateInCalendar),
        [periods, firstDateInCalendar, lastDateInCalendar],
    );
    const periodsByMonth = useMemo(() => groupPeriodsByMonth(allMonths, periods), [allMonths, periods]);

    const [focusedDate, setFocusedDate] = useState<dayjs.Dayjs | undefined>();

    const dateClickCallback = useCallback(
        (selectedDate: string) => {
            if (!setSelectedPeriods || !getSrTextForSelectedPeriod) {
                return;
            }

            if (isRangeSelection) {
                setSelectedPeriods((old) => {
                    const fom = old.length === 0 ? selectedDate : findFomDate(old[0]!, selectedDate);
                    const tom = old.length === 0 ? selectedDate : findTomDate(old[0]!, selectedDate);
                    return old.some((p) => p.fom === selectedDate || p.tom === selectedDate)
                        ? []
                        : [
                              {
                                  color: 'DARKBLUE',
                                  fom,
                                  tom,
                                  isSelected: true,
                                  srText: getSrTextForSelectedPeriod({ fom, tom }),
                              },
                          ];
                });
            } else {
                setSelectedPeriods((old) =>
                    old.some((p) => p.fom === selectedDate)
                        ? old.filter((p) => p.fom !== selectedDate)
                        : [
                              ...old,
                              {
                                  color: 'DARKBLUE',
                                  fom: selectedDate,
                                  tom: selectedDate,
                                  isSelected: true,
                                  srText: getSrTextForSelectedPeriod({ fom: selectedDate, tom: selectedDate }),
                              } satisfies CalendarPeriod,
                          ].sort(sortPeriods),
                );
            }
        },
        [isRangeSelection, getSrTextForSelectedPeriod, setSelectedPeriods],
    );

    return (
        <VStack gap="space-16">
            {!setSelectedPeriods && periods.some((p) => p.srText) && (
                <div className="sr-only">
                    {periods
                        .filter((p) => p.srText)
                        .map((p) => p.srText)
                        .toString()}
                </div>
            )}
            <HGrid gap="space-12" columns={{ sm: 1, md: setSelectedPeriods ? 1 : 2 }}>
                {allMonths.map(({ month, year }, index) => {
                    const monthPeriods = periodsByMonth.get(getMonthKey(year, month)) ?? [];
                    const isMonthInFocus = focusedDate?.year() === year && focusedDate?.month() === month;

                    return (
                        <Month
                            key={`${year}-${month}`}
                            isFirstMonth={index === 0}
                            year={year}
                            month={month}
                            periods={monthPeriods}
                            showWeekNumbers={showWeekNumbers}
                            dateTooltipCallback={dateTooltipCallback}
                            dateClickCallback={setSelectedPeriods ? dateClickCallback : undefined}
                            focusedDate={isMonthInFocus ? focusedDate : undefined}
                            setFocusedDate={setFocusedDate}
                        />
                    );
                })}
            </HGrid>
        </VStack>
    );
};

const findMonths = (
    firstDateInCalendar: string,
    lastDateInCalendar?: string,
): Array<{ month: number; year: number }> => {
    const firstDate = dayjs(firstDateInCalendar);
    const lastDate = lastDateInCalendar ? dayjs(lastDateInCalendar) : dayjs(firstDateInCalendar).add(6, 'month');

    const numberOfMonthsBetween = monthDiff(firstDate.toDate(), lastDate.toDate());

    return Array.from({ length: numberOfMonthsBetween + 1 }, (_, i) => {
        const date = firstDate.add(i, 'month');
        return { month: date.month(), year: date.year() };
    });
};

export const monthDiff = (d1: Date, d2: Date): number => {
    let months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months += d2.getMonth() - d1.getMonth();
    return Math.max(months, 0);
};

const getMonthKey = (year: number, month: number): string => `${year}-${month}`;

const groupPeriodsByMonth = (
    months: Array<{ month: number; year: number }>,
    periods: CalendarPeriod[],
): Map<string, CalendarPeriod[]> => {
    const result = new Map<string, CalendarPeriod[]>();
    for (const { year, month } of months) {
        const monthStart = dayjs().year(year).month(month).startOf('month');
        const monthEnd = monthStart.endOf('month');
        const periodsForMonth = periods.filter(
            (p) => dayjs(p.tom).isSameOrAfter(monthStart, 'day') && dayjs(p.fom).isSameOrBefore(monthEnd, 'day'),
        );
        result.set(getMonthKey(year, month), periodsForMonth);
    }
    return result;
};

const findFomDate = (period: CalendarPeriod, selectedDate: string) =>
    dayjs(period.fom).isBefore(dayjs(selectedDate)) ? period.fom : selectedDate;

const findTomDate = (period: CalendarPeriod, selectedDate: string) => {
    const parsedSelectedDate = dayjs(selectedDate);
    if (dayjs(period.tom).isAfter(parsedSelectedDate) && dayjs(period.fom).isBefore(parsedSelectedDate)) {
        return selectedDate;
    }
    return dayjs(period.tom).isBefore(parsedSelectedDate) ? selectedDate : period.tom;
};

const sortPeriods = (a: CalendarPeriod, b: CalendarPeriod) => dayjs(a.fom).diff(dayjs(b.fom));
